import React, { useCallback, useMemo } from 'react';

import { FlatList, ListRenderItemInfo, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import CheckMarkImage from 'assets/images/svgs/ico_checkmark.svg';

import gStyles from 'config/styles';

import { selectLayoutConfig } from 'store/config/selectors';
import { setPlayerSubtitlesFontSize } from 'store/player/actions';
import { selectSubtitlesFontSize } from 'store/player/selectors';
import { selectActiveUserProfile } from 'store/user/selectors';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import { isWeb } from 'utils/responsiveness';

import SettingsRowItem from '../../components/RowItem';
import WebHeader from '../../components/WebHeader';
import { SUBTITLES_FONT_SIZE_OPTIONS } from '../../constants';
import { SETTINGS_AUDIO_SUBTITLE_FONT_SIZE } from '../../languagesConst';
import { SubtitlesFontSizeSettingItem } from '../../types';

function SubtitlesFontSize() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { background } = useSelector(selectLayoutConfig);
    const selectedSubtitlesFontSize = useSelector(selectSubtitlesFontSize);
    const selectedProfileId = useSelector(selectActiveUserProfile);

    useHeaderConfig({ isRoot: isWeb });

    const listStyle = useMemo(
        () => ({
            backgroundColor: background,
        }),
        [background],
    );

    const handleChangeSubtitles = useCallback(
        ({ id }: SubtitlesFontSizeSettingItem) => {
            if (selectedProfileId) {
                dispatch(setPlayerSubtitlesFontSize(selectedProfileId, id));
            }
        },
        [dispatch, selectedProfileId],
    );

    const renderItem = useCallback(
        ({ item, index }: ListRenderItemInfo<SubtitlesFontSizeSettingItem>) => (
            <SettingsRowItem
                item={item}
                title={t(item.title)}
                onPress={handleChangeSubtitles}
                RightIcon={
                    selectedSubtitlesFontSize === item.id
                        ? CheckMarkImage
                        : undefined
                }
                position={index + 1}
                total={SUBTITLES_FONT_SIZE_OPTIONS.length}
                isSelected={selectedSubtitlesFontSize === item.id}
            />
        ),
        [handleChangeSubtitles, selectedSubtitlesFontSize, t],
    );

    return (
        <View style={[gStyles.settingsContainer, listStyle]}>
            {isWeb && (
                <WebHeader
                    title={t(SETTINGS_AUDIO_SUBTITLE_FONT_SIZE)}
                    disableFocusOnMount
                />
            )}
            <FlatList
                style={gStyles.settingsContentContainer}
                nestedScrollEnabled
                data={SUBTITLES_FONT_SIZE_OPTIONS}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                showsHorizontalScrollIndicator={false}
                accessibilityRole="menu"
            />
        </View>
    );
}

export default SubtitlesFontSize;

function keyExtractor({ id }: SubtitlesFontSizeSettingItem) {
    return id;
}
